import type { FunctionalComponent, SVGAttributes } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import { Policy } from '@/auth/Policy';
import { FULL_SCHEDULE_IN_HOME } from '@/auth/profiles';
import { SIDEBAR_EVENTS, type TrackEvent } from '@/analytics/events';

import calendarJSON from '@/assets/lotties/calendar.json';
import cashJSON from '@/assets/lotties/cash.json';
import chatJSON from '@/assets/lotties/chat.json';
import chartsJSON from '@/assets/lotties/charts.json';
import folderJSON from '@/assets/lotties/folder.json';
import homeJSON from '@/assets/lotties/home.json';
import paperJSON from '@/assets/lotties/document.json';
import percentJSON from '@/assets/lotties/percent.json';
import settingJSON from '@/assets/lotties/setting.json';
import starJSON from '@/assets/lotties/starSidebar.json';

import CalendarIcon from '@/assets/icons/Calendar.svg';
import CashIcon from '@/assets/icons/Cash.svg';
import ChartIcon from '@/assets/icons/Chart.svg';
import ChatIcon from '@/assets/icons/Chat.svg';
import FolderIcon from '@/assets/icons/Folder.svg';
import HomeIcon from '@/assets/icons/Home.svg';
import PaperIcon from '@/assets/icons/Paper.svg';
import PercentIcon from '@/assets/icons/Percent.svg';
import SettingIcon from '@/assets/icons/Setting.svg';
import StarIcon from '@/assets/icons/Star.svg';
import TwoUsersIcon from '@/assets/icons/TwoUsers.svg';

export type NavKind = 'link' | 'accordion' | 'help-center';

export interface NavItem {
  name: string;
  label: string;
  kind: NavKind;
  route?: RouteLocationRaw;
  policy: Policy;
  weight: number;
  lottie?: object;
  lottieSize?: number;
  icon?: FunctionalComponent<SVGAttributes>;
  analyticsEvent: TrackEvent;
  children?: NavItem[];
}

export const NAVIGATION: readonly NavItem[] = [
  {
    name: 'home',
    analyticsEvent: SIDEBAR_EVENTS.home,
    label: 'Inicio',
    kind: 'link',
    route: { name: 'home' },
    policy: Policy.always(),
    weight: 10,
    lottie: homeJSON,
    icon: HomeIcon,
  },
  {
    name: 'deals',
    analyticsEvent: SIDEBAR_EVENTS.deals,
    label: 'Mis ofertas',
    kind: 'link',
    route: { name: 'deals' },
    policy: Policy.capability('deal:read').and(Policy.feature('deals')),
    weight: 20,
    lottie: percentJSON,
    lottieSize: 28,
    icon: PercentIcon,
  },
  {
    name: 'schedule',
    analyticsEvent: SIDEBAR_EVENTS.schedule,
    label: 'Agenda',
    kind: 'link',
    route: { name: 'schedule' },
    policy: Policy.capability('booking:read').and(FULL_SCHEDULE_IN_HOME.not()),
    weight: 30,
    lottie: calendarJSON,
    icon: CalendarIcon,
  },
  {
    name: 'liquidations',
    analyticsEvent: SIDEBAR_EVENTS.liquidations,
    label: 'Facturación',
    kind: 'link',
    route: { name: 'liquidations' },
    policy: Policy.capability('billing:read'),
    weight: 40,
    lottie: paperJSON,
    icon: PaperIcon,
  },
  {
    name: 'reservation-configuration',
    analyticsEvent: SIDEBAR_EVENTS.reservationConfiguration,
    label: 'Configurar reservas',
    kind: 'link',
    route: { name: 'reservation-locations' },
    policy: Policy.capability('reservation:configure'),
    weight: 50,
    lottie: settingJSON,
    icon: SettingIcon,
  },
  {
    name: 'experiences',
    analyticsEvent: SIDEBAR_EVENTS.experiences,
    label: 'Experiencias',
    kind: 'accordion',
    route: { name: 'my-experiences' },
    policy: Policy.capability('experience:read'),
    weight: 60,
    lottie: folderJSON,
    icon: FolderIcon,
    children: [
      {
        name: 'my-experiences',
        analyticsEvent: SIDEBAR_EVENTS.myExperiences,
        label: 'Mis experiencias',
        kind: 'link',
        route: { name: 'my-experiences' },
        policy: Policy.capability('experience:read'),
        weight: 10,
      },
      {
        name: 'social-media-covers',
        analyticsEvent: SIDEBAR_EVENTS.socialMediaCovers,
        label: 'Mi contenido',
        kind: 'link',
        route: { name: 'social-media-covers' },
        policy: Policy.capability('experience:read'),
        weight: 20,
      },
    ],
  },
  {
    name: 'reviews',
    analyticsEvent: SIDEBAR_EVENTS.reviews,
    label: 'Reseñas',
    kind: 'link',
    route: { name: 'reviews' },
    policy: Policy.capability('review:read'),
    weight: 70,
    lottie: starJSON,
    icon: StarIcon,
  },
  {
    name: 'charts',
    analyticsEvent: SIDEBAR_EVENTS.charts,
    label: 'Métricas',
    kind: 'link',
    route: { name: 'charts' },
    policy: Policy.capability('metrics:read'),
    weight: 80,
    lottie: chartsJSON,
    icon: ChartIcon,
  },
  {
    name: 'benefits',
    analyticsEvent: SIDEBAR_EVENTS.benefits,
    label: 'Beneficios',
    kind: 'link',
    route: { name: 'benefits' },
    policy: Policy.capability('benefit:read'),
    weight: 90,
    lottie: cashJSON,
    icon: CashIcon,
  },
  {
    name: 'users-management',
    analyticsEvent: SIDEBAR_EVENTS.usersManagement,
    label: 'Gestionar usuarios',
    kind: 'link',
    route: { name: 'users-management' },
    policy: Policy.capability('users:manage'),
    weight: 95,
    icon: TwoUsersIcon,
  },
  {
    name: 'help-center',
    analyticsEvent: SIDEBAR_EVENTS.helpCenter,
    label: 'Centro de ayuda',
    kind: 'help-center',
    policy: Policy.always(),
    weight: 100,
    lottie: chatJSON,
    icon: ChatIcon,
  },
];
